/* Gemlikzade — collection filters, sorting and load-more via the Section Rendering API.
   Without JS everything still works (GET form + plain links). */
if (!customElements.get('gz-collection')) {
  class GzCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.filters = this.querySelector('[data-gz-filters]');
      this.form = this.querySelector('[data-gz-filter-form]');
      this.sort = this.querySelector('[data-gz-sort]');
      this.openBtn = this.querySelector('[data-gz-filters-open]');
      this.desktopSidebar = window.matchMedia('(min-width: 990px)');
      this.isSidebar = this.classList.contains('gz-coll__layout--sidebar');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.onKeydown = this.onKeydown.bind(this);

      // The sort select is associated with the filter form via form="", so the hidden fallback is not needed with JS.
      this.form?.querySelector('input[type="hidden"][name="sort_by"]')?.remove();

      this.openBtn?.addEventListener('click', () => this.open());
      this.querySelectorAll('[data-gz-filters-close]').forEach((el) => el.addEventListener('click', () => this.close()));

      if (this.form) {
        this.form.addEventListener('change', (event) => {
          const isPrice = event.target.type === 'number';
          window.clearTimeout(this.timer);
          this.timer = window.setTimeout(() => this.render(this.buildUrl()), isPrice ? 700 : 120);
        });
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          window.clearTimeout(this.timer);
          this.render(this.buildUrl());
          this.close();
        });
      }
      // The sort select lives outside the <form> element (linked with form=""), so its change
      // event never reaches the form listener — listen to it directly.
      this.sort?.addEventListener('change', () => {
        window.clearTimeout(this.timer);
        this.render(this.buildUrl());
      });

      this.addEventListener('click', (event) => {
        const filterLink = event.target.closest('[data-gz-filter-link]');
        if (filterLink) {
          event.preventDefault();
          this.render(filterLink.href);
          return;
        }
        const more = event.target.closest('[data-gz-more]');
        if (more) {
          event.preventDefault();
          this.loadMore(more);
        }
      });

      this.syncDialogRole();
      this.desktopSidebar.addEventListener?.('change', () => this.syncDialogRole());
      window.addEventListener('popstate', () => this.render(window.location.href, false));
    }

    /* ---------- Drawer ---------- */
    syncDialogRole() {
      if (!this.filters) return;
      const inline = this.isSidebar && this.desktopSidebar.matches;
      if (inline) {
        this.filters.removeAttribute('role');
        this.filters.removeAttribute('aria-modal');
        this.close(false);
      } else {
        this.filters.setAttribute('role', 'dialog');
        this.filters.setAttribute('aria-modal', 'true');
      }
    }

    open() {
      if (!this.filters) return;
      this.lastFocus = document.activeElement;
      this.filters.classList.add('is-open');
      this.openBtn?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);
      window.setTimeout(() => (this.filters.querySelector('summary, input, button') || this.filters).focus({ preventScroll: true }), this.reduceMotion ? 0 : 120);
    }

    close(restoreFocus = true) {
      if (!this.filters || !this.filters.classList.contains('is-open')) return;
      this.filters.classList.remove('is-open');
      this.openBtn?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeydown);
      if (restoreFocus) this.lastFocus?.focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusables = [...this.filters.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, summary')].filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------- Rendering ---------- */
    buildUrl() {
      const params = new URLSearchParams();
      if (this.form) {
        for (const [key, value] of new FormData(this.form)) {
          if (value !== '') params.append(key, value);
        }
      } else if (this.sort) {
        params.set('sort_by', this.sort.value);
      }
      const query = params.toString();
      return `${window.location.pathname}${query ? `?${query}` : ''}`;
    }

    async fetchSection(url) {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('section_id', this.sectionId);
      const response = await fetch(target.toString());
      const text = await response.text();
      return new DOMParser().parseFromString(text, 'text/html');
    }

    async render(url, pushState = true) {
      const results = this.querySelector('[data-gz-results]');
      results?.classList.add('is-loading');
      try {
        const doc = await this.fetchSection(url);
        const incoming = doc.querySelector('gz-collection');
        if (!incoming) throw new Error('missing section');

        // Results (active chips, grid, pagination)
        const newResults = incoming.querySelector('[data-gz-results]');
        if (results && newResults) results.innerHTML = newResults.innerHTML;

        // Count + active badge + apply label
        this.swapText(incoming, '[data-gz-count]');
        this.swapText(incoming, '[data-gz-apply-label]');
        const badge = this.querySelector('[data-gz-active-count]');
        const newBadge = incoming.querySelector('[data-gz-active-count]');
        if (badge && newBadge) {
          badge.textContent = newBadge.textContent;
          badge.hidden = newBadge.hasAttribute('hidden');
        }

        // Filter values (counts / disabled states) while keeping open panels and focus
        const body = this.querySelector('[data-gz-filter-body]');
        const newBody = incoming.querySelector('[data-gz-filter-body]');
        if (body && newBody) {
          const openIndexes = [...body.querySelectorAll('details[open]')].map((d) => d.dataset.index);
          const focusedName = document.activeElement?.name;
          const focusedValue = document.activeElement?.value;
          const isNumber = document.activeElement?.type === 'number';
          body.innerHTML = newBody.innerHTML;
          body.querySelectorAll('details').forEach((d) => { d.open = openIndexes.includes(d.dataset.index) || d.open; });
          if (focusedName && !isNumber) {
            const match = [...body.querySelectorAll(`[name="${CSS.escape(focusedName)}"]`)].find((el) => el.value === focusedValue);
            match?.focus({ preventScroll: true });
          } else if (isNumber && focusedName) {
            body.querySelector(`[name="${CSS.escape(focusedName)}"]`)?.focus({ preventScroll: true });
          }
        }

        // Sort select value
        const newSort = incoming.querySelector('[data-gz-sort]');
        if (this.sort && newSort) this.sort.value = newSort.value;

        if (pushState) {
          const clean = new URL(url, window.location.origin);
          clean.searchParams.delete('section_id');
          clean.searchParams.delete('page');
          window.history.pushState({}, '', clean.pathname + clean.search);
        }
      } catch (error) {
        window.location.href = url;
      } finally {
        results?.classList.remove('is-loading');
      }
    }

    swapText(incoming, selector) {
      const current = this.querySelector(selector);
      const next = incoming.querySelector(selector);
      if (current && next) current.textContent = next.textContent;
    }

    async loadMore(button) {
      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');
      try {
        const doc = await this.fetchSection(button.href);
        const grid = this.querySelector('[data-gz-grid]');
        const newItems = doc.querySelectorAll('[data-gz-grid] > .gz-coll-grid__item');
        const firstNew = newItems[0];
        newItems.forEach((item) => grid.appendChild(item));
        const wrap = this.querySelector('[data-gz-more-wrap]');
        const newWrap = doc.querySelector('[data-gz-more-wrap]');
        if (wrap && newWrap) wrap.innerHTML = newWrap.innerHTML;
        // Move focus to the first new product for keyboard / screen reader users.
        firstNew?.querySelector('.gz-card__title a')?.focus({ preventScroll: true });
      } catch (error) {
        window.location.href = button.href;
      }
    }
  }
  customElements.define('gz-collection', GzCollection);
}
